import type { Role } from '@cmoh/shared'
import { hasAtLeast } from '@cmoh/shared'
import type { RouteMeta } from './routing/routing.types'

/**
 * Evaluates if a user has permission to access a route based on RBAC rules.
 *
 * This function centralizes the permission evaluation logic used by both
 * the routing system and sidebar navigation to ensure consistency.
 *
 * @param meta - Route metadata containing permission requirements
 * @param user - User object with authentication and role information
 * @param userRole - User's current role in the organization
 * @returns True if user has permission, false otherwise
 */
export function evaluatePermission(
  meta: RouteMeta | undefined,
  user: { isSuperAdmin?: boolean } | null,
  userRole: Role | undefined,
): boolean {
  if (!meta) return true
  if (meta.public) return true
  if (!user) return false

  const isSuperAdmin = Boolean(user.isSuperAdmin)
  if (meta.require?.superAdminOnly) {
    return isSuperAdmin
  }
  if (isSuperAdmin && meta.require?.superAdminBypass !== false) {
    return true
  }
  if (!userRole) return false

  const { roles, minRole } = meta.require || {}
  if (roles && roles.length > 0) {
    return roles.includes(userRole)
  }
  if (minRole) {
    return hasAtLeast(userRole, minRole)
  }
  return true
}
